import { useState, useEffect, useRef } from 'react'
import Parse from 'parse'
import { CompanyService } from '../services/CompanyService'
import { usePermissionStore } from '../stores/permissionStore'
import authService from '../services/authService'

interface CompanyPermissions {
  hasCompanyAccess: boolean
  isCompanyManagerOrAdmin: boolean
  isGlobalAdmin: boolean
  currentCompany: any | null
  /**
   * The company whose roles and permissions the RBAC store follows (SC-439). It is
   * `currentCompany`, except for a platform admin: `currentCompany` stays null for them, but an
   * admin who also belongs to a company (and may be its Owner) needs that company's answer, or
   * the Owner controls on their own roster never show. The server says whether they are a member.
   */
  rbacCompanyId: string | null
  loading: boolean
  error: string | null
  // New fields for company disabled handling
  isCompanyDisabled: boolean
  canCreateRooms: boolean
  canJoinRooms: boolean
  canAccessDashboard: boolean
  canAccessCompanyManagement: boolean
  disabledReason: string | null
  /**
   * The last check could not get an answer (session check timed out, lookup failed). The company
   * fields keep their previous values, so callers must not read `hasCompanyAccess: false` as
   * "belongs to no company" while this is true.
   */
  companyUnknown: boolean
  // Function to refresh permissions
  refreshPermissions: () => Promise<void>
}

/** When a company check got no answer: three more tries, then only a tab return asks again. */
const UNKNOWN_RETRY_DELAYS_MS = [5_000, 20_000, 60_000]

export const useCompanyPermissions = (): CompanyPermissions => {
  const adminCapabilitiesVerified = usePermissionStore((s) => s.adminCapabilitiesVerified)
  const adminCapabilitiesLoading = usePermissionStore((s) => s.adminCapabilitiesLoading)
  const storeElevatedAdmin = usePermissionStore((s) => s.isGlobalAdmin || s.isPlatformAdmin)

  const [hasCompanyAccess, setHasCompanyAccess] = useState(false)
  const [isCompanyManagerOrAdmin, setIsCompanyManagerOrAdmin] = useState(false)
  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false)
  const [currentCompany, setCurrentCompany] = useState<any>(null)
  const [adminOwnCompanyId, setAdminOwnCompanyId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // New state for company disabled handling
  const [isCompanyDisabled, setIsCompanyDisabled] = useState(false)
  const [canCreateRooms, setCanCreateRooms] = useState(true)
  const [canJoinRooms, setCanJoinRooms] = useState(true)
  const [canAccessDashboard, setCanAccessDashboard] = useState(true)
  const [canAccessCompanyManagement, setCanAccessCompanyManagement] = useState(true)
  const [disabledReason, setDisabledReason] = useState<string | null>(null)
  const [companyUnknown, setCompanyUnknownState] = useState(false)
  const companyUnknownRef = useRef(false)

  // Add caching and debouncing
  const lastCheckTime = useRef<number>(0)
  const isChecking = useRef<boolean>(false)
  const CACHE_DURATION = 5000 // 5 seconds cache

  const retryAttempt = useRef(0)
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const setCompanyUnknown = (unknown: boolean) => {
    companyUnknownRef.current = unknown
    setCompanyUnknownState(unknown)
    if (!unknown) retryAttempt.current = 0
  }

  // Bounded retries (SC-477): a failed lookup is never cached, so each attempt is one cloud call.
  // A hidden tab skips its turn; the visibility handler below asks once it is shown again.
  const scheduleRetry = () => {
    if (retryTimer.current || retryAttempt.current >= UNKNOWN_RETRY_DELAYS_MS.length) return
    const delay = UNKNOWN_RETRY_DELAYS_MS[retryAttempt.current]
    retryAttempt.current += 1
    retryTimer.current = setTimeout(() => {
      retryTimer.current = null
      if (document.visibilityState === 'hidden') return
      void checkCompanyPermissions(true)
    }, delay)
  }

  const markCompanyUnknown = (reason: string) => {
    console.warn(`[useCompanyPermissions] Company unknown (${reason}); not treating it as "no company"`)
    setCompanyUnknown(true)
    scheduleRetry()
  }

  const checkCompanyPermissions = async (forceRefresh: boolean = false) => {
    // Check if we're already checking or if we have recent cached data
    const now = Date.now()
    if (!forceRefresh && (isChecking.current || (now - lastCheckTime.current < CACHE_DURATION))) {
      console.log('[useCompanyPermissions] Skipping permission check - cached or already checking')
      return
    }

    try {
      isChecking.current = true
      setLoading(true)
      setError(null)

      console.log('[useCompanyPermissions] Starting permission check...')

      const sessionUser = await authService.waitForSessionCheck()
      if (!sessionUser || !Parse.User.current()?.getSessionToken()) {
        // Still holding a token means the check gave up on a slow Back4App, not a sign-out.
        if (Parse.User.current()?.getSessionToken()) markCompanyUnknown('session check timed out')
        setLoading(false)
        isChecking.current = false
        return
      }

      const companyResult = await CompanyService.getCurrentUserCompany()

      const perm = usePermissionStore.getState()
      const isGlobalAdmin =
        perm.adminCapabilitiesVerified &&
        (perm.isGlobalAdmin || perm.isPlatformAdmin)
      setIsGlobalAdmin(isGlobalAdmin)

      console.log('[useCompanyPermissions] Permission check results:', {
        isGlobalAdmin,
        companySuccess: companyResult.success
      })

      // If user is global admin, they have access regardless of company association
      if (isGlobalAdmin) {
        console.log('[useCompanyPermissions] User is global admin, granting full access')
        setCompanyUnknown(false)
        setHasCompanyAccess(true)
        setIsCompanyManagerOrAdmin(true)
        setCurrentCompany(null) // Global admins don't need a specific company
        // The company lookup above is already made (and shared), so this adds no request here.
        setAdminOwnCompanyId(companyResult.success ? companyResult.company?.id ?? null : null)
        setIsCompanyDisabled(false)
        setCanCreateRooms(true)
        setCanJoinRooms(true)
        setCanAccessDashboard(true)
        setCanAccessCompanyManagement(true)
        setDisabledReason(null)
        setLoading(false)
        return
      }

      setAdminOwnCompanyId(null)

      if (companyResult.lookupFailed) {
        markCompanyUnknown(companyResult.error || 'lookup failed')
        setLoading(false)
        return
      }
      setCompanyUnknown(false)

      // Use the company result we already fetched
      console.log('[useCompanyPermissions] User is not global admin, processing company result...')

      console.log('[useCompanyPermissions] Company result:', companyResult)
      console.log('[useCompanyPermissions] Company result details:', {
        success: companyResult.success,
        company: companyResult.company,
        user_role: companyResult.user_role,
        error: companyResult.error
      })

      // Check if company is disabled (including simulated for testing)
      const isSimulatedDisabled = process.env.NODE_ENV !== 'production' && localStorage.getItem('simulateDisabledCompany') === 'true'

      // Check if company is disabled based on error message
      const isDisabledByError = companyResult.error && companyResult.error.includes('disabled')

      if (isDisabledByError || isSimulatedDisabled || (!companyResult.success && companyResult.company)) {
        // Company is disabled
        setHasCompanyAccess(false)
        setIsCompanyManagerOrAdmin(false)
        setCurrentCompany(companyResult.company)
        setIsCompanyDisabled(true)
        setCanCreateRooms(false)
        setCanJoinRooms(false)
        setCanAccessDashboard(false)
        setCanAccessCompanyManagement(true) // Still allow access to company management for admins
        setDisabledReason(companyResult.error || 'Company is disabled')
        setError(companyResult.error || 'Company is disabled')
        console.log('[useCompanyPermissions] Company is disabled:', companyResult.error)

        // Set loading to false and return early since we've handled the disabled case
        setLoading(false)
        return
      } else if (companyResult.success && companyResult.company) {
        // Company is active
        setCurrentCompany(companyResult.company)
        setHasCompanyAccess(true)
        setIsCompanyDisabled(false)
        setCanCreateRooms(true)
        setCanJoinRooms(true)
        setCanAccessDashboard(true)
        setCanAccessCompanyManagement(true)
        setDisabledReason(null)

        // Check if user is company manager or admin
        const userRole = companyResult.user_role || 'user'
        const isManager = userRole === 'manager' || userRole === 'admin'
        setIsCompanyManagerOrAdmin(isManager)

        console.log('[useCompanyPermissions] User has company access:', {
          company: companyResult.company.name,
          role: userRole,
          isManager
        })

        // Set loading to false and return early since we have successful company access
        setLoading(false)
        return
      } else {
        // Check if we have a disabled company error but no company data
        if (companyResult.error && companyResult.error.includes('disabled')) {
          // Company is disabled but we don't have company data
          setHasCompanyAccess(false)
          setIsCompanyManagerOrAdmin(false)
          setCurrentCompany(null) // We don't have company data
          setIsCompanyDisabled(true)
          setCanCreateRooms(false)
          setCanJoinRooms(false)
          setCanAccessDashboard(false)
          setCanAccessCompanyManagement(false) // No company data, so no management access
          setDisabledReason(companyResult.error)
          setError(companyResult.error)
          console.log('[useCompanyPermissions] Company is disabled (no company data):', companyResult.error)

          // Set loading to false and return early since we've handled the disabled case
          setLoading(false)
          return
        } else {
          // No company association → treat as standalone user (not disabled)
          setHasCompanyAccess(false)
          setIsCompanyManagerOrAdmin(false)
          setCurrentCompany(null)
          setIsCompanyDisabled(false)
          // Company-scoped abilities remain false, but dashboard access should be allowed
          setCanCreateRooms(false)
          setCanJoinRooms(true)
          setCanAccessDashboard(true)
          setCanAccessCompanyManagement(false)
          setDisabledReason(null)

          // Set loading to false and return early since we've handled the no company case
          setLoading(false)
          return
        }
      }
    } catch (error) {
      console.error('[useCompanyPermissions] Error checking permissions:', error)
      markCompanyUnknown('error checking permissions')
    } finally {
      setLoading(false)
      isChecking.current = false
      lastCheckTime.current = Date.now()
    }
  }

  useEffect(() => {
    // Safety timeout: if the permission check hangs (slow Back4App),
    // stop the loading spinner after 10 s so the UI is never stuck.
    const MOUNT_TIMEOUT_MS = 10000
    let timedOut = false

    const safeCheck = async () => {
      const timeout = setTimeout(() => {
        timedOut = true
        console.warn('[useCompanyPermissions] Mount timeout reached — proceeding with defaults')
        setLoading(false)
        isChecking.current = false
      }, MOUNT_TIMEOUT_MS)

      try {
        await checkCompanyPermissions()
      } finally {
        clearTimeout(timeout)
        if (timedOut) {
          setLoading(false)
        }
      }
    }

    safeCheck()
  }, [])

  useEffect(() => {
    void checkCompanyPermissions(true)
    // Re-run when server-attested admin flags settle
  }, [adminCapabilitiesVerified, storeElevatedAdmin])

  useEffect(() => {
    // A tab shown again while the company is still unknown asks once, unless a retry is pending.
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      if (companyUnknownRef.current && !retryTimer.current) void checkCompanyPermissions(true)
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      if (retryTimer.current) clearTimeout(retryTimer.current)
      retryTimer.current = null
    }
  }, [])

  const capsPending =
    !!Parse.User.current() &&
    !adminCapabilitiesVerified &&
    adminCapabilitiesLoading

  return {
    hasCompanyAccess,
    isCompanyManagerOrAdmin,
    isGlobalAdmin,
    currentCompany,
    rbacCompanyId: currentCompany?.id ?? adminOwnCompanyId,
    loading: loading || capsPending,
    error,
    isCompanyDisabled,
    canCreateRooms,
    canJoinRooms,
    canAccessDashboard,
    canAccessCompanyManagement,
    disabledReason,
    companyUnknown,
    refreshPermissions: () => checkCompanyPermissions(true)
  }
} 